import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, User as UserIcon, LogOut, ChevronDown, Search, Sun, Moon, Globe, FileUp } from "lucide-react";
import { NxAvatar } from "@/components/ui/NxAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { apiGet } from "@/lib/api";
import pt from "@/lib/i18n";
import { InvoiceUploadModal } from "@/components/idp/InvoiceUploadModal";

const TITLES = {
    "/dashboard": pt.nav.dashboard,
    "/admin/utilizadores": pt.users.title,
    "/perfil": pt.profile.title,
    "/servicos": pt.nav.services,
    "/servicos/nova": pt.services.new,
    "/clientes": pt.nav.customers,
    "/tickets": pt.tickets.title,
    "/operacoes-campo": pt.nav.fieldOps,
    "/servico-cliente": pt.nav.cx,
    "/conversas": pt.conversations.title,
    "/base-conhecimento": pt.kb.title,
    "/vendas": pt.sales.title,
    "/vendas/copiloto": pt.copilot.title,
    "/vendas/propostas": pt.proposals.title,
    "/receita": pt.revenue.title,
    "/analitica": pt.nav.analytics,
    "/integracoes": pt.nav.integrations,
};

function _resolveTitle(pathname) {
    if (TITLES[pathname]) return TITLES[pathname];
    // Prefix match for tickets/:id, servicos/:id, etc.
    const keys = Object.keys(TITLES).sort((a, b) => b.length - a.length);
    for (const k of keys) {
        if (pathname.startsWith(k + "/") || pathname === k) return TITLES[k];
    }
    return pt.nav.dashboard;
}

export const TopBar = ({ onOpenNotifications, unreadCount: unreadCountProp }) => {
    const { user, logout } = useAuth();
    const { theme, toggle: toggleTheme } = useTheme();
    const { lang, toggle: toggleLang } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [unread, setUnread] = useState(unreadCountProp ?? 0);
    const [wiggle, setWiggle] = useState(false);
    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const menuRef = useRef(null);
    const initialLoad = useRef(true);

    const title = _resolveTitle(location.pathname);

    useEffect(() => {
        let mounted = true;
        const fetch = async () => {
            try {
                const data = await apiGet("/notifications/unread-count");
                if (!mounted) return;
                const next = data?.count ?? 0;
                if (!initialLoad.current && next > unread) setWiggle(true);
                initialLoad.current = false;
                setUnread(next);
            } catch (e) {
                /* ignore */
            }
        };
        fetch();
        const id = setInterval(fetch, 30000);
        return () => {
            mounted = false;
            clearInterval(id);
        };
    }, [unread]);

    useEffect(() => {
        if (unreadCountProp != null) setUnread(unreadCountProp);
    }, [unreadCountProp]);

    useEffect(() => {
        if (!wiggle) return;
        const t = setTimeout(() => setWiggle(false), 700);
        return () => clearTimeout(t);
    }, [wiggle]);

    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        window.addEventListener("mousedown", handler);
        return () => window.removeEventListener("mousedown", handler);
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <header
            className="sticky top-0 z-20 flex items-center justify-between gap-6 px-8 py-3"
            style={{
                background: "var(--bg-surface)",
                backdropFilter: "blur(20px) saturate(1.4)",
                borderBottom: "1px solid var(--glass-border)",
            }}
            data-testid="topbar"
        >
            <div className="flex items-center gap-3">
                <h2 className="font-display text-xl text-[color:var(--slate-deep)]">{title}</h2>
            </div>

            <div className="flex items-center gap-3">
                <div
                    className="hidden md:flex items-center gap-2 rounded-full px-3.5 py-1.5"
                    style={{
                        background: "var(--bg-base)",
                        border: "1px solid var(--glass-border)",
                    }}
                >
                    <Search size={14} className="text-[color:var(--slate-light)]" />
                    <input
                        type="text"
                        placeholder={pt.topBar?.search || "Pesquisar…"}
                        className="bg-transparent border-0 outline-none text-sm text-[color:var(--slate-deep)] placeholder:text-[color:var(--slate-light)] w-44"
                        data-testid="topbar-search"
                    />
                </div>

                <button
                    type="button"
                    onClick={() => setInvoiceModalOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors text-[11px] font-display"
                    style={{ background: "var(--copper-subtle)", color: "var(--copper-dark)",
                             border: "1px solid var(--copper)" }}
                    aria-label={pt.invoiceModal?.trigger || "Upload Invoice"}
                    data-testid="invoice-upload-trigger"
                    title={pt.invoiceModal?.trigger || "Upload Invoice"}
                >
                    <FileUp size={13} strokeWidth={1.9} />
                    <span className="hidden sm:inline">{pt.invoiceModal?.trigger || "Upload Invoice"}</span>
                </button>

                <button
                    type="button"
                    onClick={toggleLang}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full hover:bg-[color:var(--copper-subtle)] transition-colors"
                    aria-label="Toggle language"
                    data-testid="lang-quick-toggle"
                    title={lang === "en" ? pt.common.switchLangToPt : pt.common.switchLangToEn}
                >
                    <Globe size={14} strokeWidth={1.7} className="text-[color:var(--copper)]" />
                    <span className="text-[11px] font-mono font-semibold tracking-wider text-[color:var(--slate-deep)]">
                        {lang.toUpperCase()}
                    </span>
                </button>

                <button
                    type="button"
                    onClick={toggleTheme}
                    className="p-2 rounded-full hover:bg-[color:var(--copper-subtle)] transition-colors"
                    aria-label={theme === "dark" ? "Mudar para tema claro" : "Mudar para tema escuro"}
                    data-testid="theme-toggle"
                >
                    {theme === "dark" ? (
                        <Sun size={20} strokeWidth={1.6} className="text-[color:var(--copper-light)]" />
                    ) : (
                        <Moon size={20} strokeWidth={1.6} className="text-[color:var(--slate-deep)]" />
                    )}
                </button>

                <button
                    type="button"
                    onClick={onOpenNotifications}
                    className="relative p-2 rounded-full hover:bg-[color:var(--copper-subtle)] transition-colors"
                    aria-label={pt.notifications.title}
                    data-testid="notifications-bell"
                >
                    <Bell
                        size={20}
                        strokeWidth={1.6}
                        className={"text-[color:var(--slate-deep)] " + (wiggle ? "bell-wiggle" : "")}
                    />
                    {unread > 0 && (
                        <span
                            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full font-mono text-[10px] font-semibold text-white"
                            style={{ background: "var(--copper)" }}
                            data-testid="notifications-badge"
                        >
                            {unread > 99 ? "99+" : unread}
                        </span>
                    )}
                </button>

                <div className="relative" ref={menuRef}>
                    <button
                        type="button"
                        onClick={() => setMenuOpen((o) => !o)}
                        className="flex items-center gap-2 p-1 pr-3 rounded-full hover:bg-[color:var(--copper-subtle)] transition-colors"
                        data-testid="user-menu-trigger"
                    >
                        <NxAvatar name={user?.full_name} size="md" />
                        <span className="hidden md:inline font-body text-sm text-[color:var(--slate-deep)] font-medium">
                            {user?.full_name?.split(" ")[0]}
                        </span>
                        <ChevronDown size={14} className="text-[color:var(--slate-mid)]" />
                    </button>
                    {menuOpen && (
                        <div
                            className="absolute right-0 mt-2 w-56 rounded-xl py-1.5 glass-elevated"
                            data-testid="user-menu"
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    setMenuOpen(false);
                                    navigate("/perfil");
                                }}
                                className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-[color:var(--copper-subtle)] text-[color:var(--slate-deep)] transition-colors"
                                data-testid="user-menu-profile"
                            >
                                <UserIcon size={15} strokeWidth={1.6} />
                                {pt.nav.profile}
                            </button>
                            <div className="my-1 h-px bg-[color:var(--glass-border)]" />
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-2.5 hover:bg-[rgba(192,57,43,0.08)] text-[color:var(--error)] transition-colors"
                                data-testid="user-menu-logout"
                            >
                                <LogOut size={15} strokeWidth={1.6} />
                                {pt.nav.logout}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <InvoiceUploadModal open={invoiceModalOpen} onClose={() => setInvoiceModalOpen(false)} />
        </header>
    );
};
